import React, { useEffect, useRef, useState } from 'react';
import styles from './EditionCard.module.css';
import { Conversion } from '../../types';
import type { ShareResult } from '../../api/conversions';

export interface Edition {
  id: string;
  serialNumber: string;    // full form, e.g. "NCT-20260414-042"
  editionNumber: number;   // e.g. 42
  title: string;
  author?: string | null;  // credited author/creator, shown when present
  source: string;          // publication name, e.g. "The Pragmatic Engineer"
  sourceUrl: string;
  durationSeconds: number;
  publishedAt: string;     // ISO date string — "Prepared" date
  status: 'ready' | 'processing' | 'queued' | 'failed' | 'expired';
  /** ISO date when the audio is removed by the retention rule. */
  expiresAt?: string | null;
  remainingSeconds?: number; // set while playing; enables "m:ss remaining"
  /** True once the owner has flagged this edition for admin review. */
  reported?: boolean;
}

interface EditionCardProps {
  edition: Edition;
  /** Active state — when true, the card is styled as "Now playing". */
  isActive?: boolean;
  /** Edition variant label appended to the serial line. */
  editionType?: string;
  onPlay?: (id: string) => void;
  onDelete?: (id: string) => void;
  onViewTranscript?: (id: string) => void;
  /** Share handler — resolves with how the share was delivered. */
  onShare?: (id: string) => Promise<ShareResult> | void;
  /** Download handler — resolves once the MP3 has been saved to disk. */
  onDownload?: (id: string) => Promise<unknown> | void;
  /** Report handler — opens the "flag for review" flow. */
  onReport?: (id: string) => void;
  /** Renew handler — regenerates an expired edition at no cost. */
  onRenew?: (id: string) => Promise<unknown> | void;
}

function formatDurationMinutes(seconds: number): string {
  const mins = Math.max(0, Math.round(seconds / 60));
  return `${mins} min`;
}

function formatRemaining(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')} remaining`;
}

function formatShortDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

function editionNumberLabel(n: number): string {
  return `NCT-${String(n).padStart(3, '0')}`;
}

// Schemes that execute or embed content in the browser — never linked.
// text: is the backend's synthetic placeholder for sourceless submissions.
const NON_LINK_PROTOCOLS = new Set(['javascript:', 'data:', 'vbscript:', 'text:']);

function parseUrl(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

function extractSourceName(url: string): string {
  const parsed = parseUrl(url);
  if (!parsed) return 'Unknown source';
  if (parsed.protocol === 'text:') return 'Text submission';
  if (parsed.protocol === 'mailto:') {
    // Surface the actual address so users can tell which inbox or pasted
    // submission an edition came from. Falls back to the generic label if
    // the URL has no address part.
    const address = url.slice('mailto:'.length).split('?')[0].trim();
    return address || 'Email';
  }
  if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
    return parsed.hostname.replace(/^www\./, '');
  }
  // Any other scheme: show the link as-is, whichever client produced it.
  return url;
}

function isLinkableUrl(url: string): boolean {
  const parsed = parseUrl(url);
  return !!parsed && !NON_LINK_PROTOCOLS.has(parsed.protocol);
}

function toSerial(dateIso: string, n: number): string {
  const d = new Date(dateIso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `NCT-${y}${m}${day}-${String(n).padStart(3, '0')}`;
}

export function conversionToEdition(c: Conversion, editionNumber: number): Edition {
  const status: Edition['status'] =
    c.status === 'completed'
      ? 'ready'
      : c.status === 'expired'
        ? 'expired'
        : c.status === 'failed'
          ? 'failed'
          : c.status === 'processing'
            ? 'processing'
            : 'queued';
  return {
    id: c.id,
    serialNumber: toSerial(c.createdAt, editionNumber),
    editionNumber,
    title: c.title?.trim() || 'Untitled conversion',
    author: c.author,
    source: extractSourceName(c.sourceUrl || c.url),
    sourceUrl: c.sourceUrl || c.url,
    durationSeconds: c.durationSeconds ?? 0,
    publishedAt: c.createdAt,
    status,
    reported: !!c.reportStatus,
    expiresAt: c.expiresAt ?? null,
  };
}

export default function EditionCard({
  edition,
  isActive = false,
  editionType = 'Text to speech',
  onPlay,
  onDelete,
  onViewTranscript,
  onShare,
  onDownload,
  onReport,
  onRenew,
}: EditionCardProps) {
  const cardClass = [styles.card, isActive ? styles.cardActive : ''].filter(Boolean).join(' ');

  /* The status label is rendered as an LED indicator — dot + label,
     tone keyed to the state. */
  const stateToneClass = isActive
    ? styles.statePlaying
    : edition.status === 'ready'
      ? styles.stateReady
      : edition.status === 'failed' || edition.status === 'expired'
        ? styles.stateFailed
        : edition.status === 'processing'
          ? styles.stateProcessing
          : styles.stateQueued;

  const stateClass = `${styles.stateLabel} ${stateToneClass}`;

  const [shareState, setShareState] = useState<'idle' | 'sharing' | 'copied'>('idle');
  const shareTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading'>('idle');
  const [renewState, setRenewState] = useState<'idle' | 'renewing'>('idle');

  useEffect(() => {
    return () => {
      if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);
    };
  }, []);

  const handleDownloadClick = async () => {
    if (!onDownload || downloadState === 'downloading') return;
    setDownloadState('downloading');
    try {
      await onDownload(edition.id);
    } catch {
      // Best effort — fall back to idle and let the page surface errors.
    } finally {
      setDownloadState('idle');
    }
  };

  const downloadLabel = downloadState === 'downloading' ? 'Downloading…' : 'Download';

  const handleRenewClick = async () => {
    if (!onRenew || renewState === 'renewing') return;
    setRenewState('renewing');
    try {
      await onRenew(edition.id);
    } catch {
      // The page surfaces the error; just reset the button.
    } finally {
      setRenewState('idle');
    }
  };

  const handleShareClick = async () => {
    if (!onShare || shareState === 'sharing') return;
    setShareState('sharing');
    try {
      const result = await onShare(edition.id);
      if (result === 'copied') {
        setShareState('copied');
        if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);
        shareTimeoutRef.current = setTimeout(() => setShareState('idle'), 2000);
      } else {
        setShareState('idle');
      }
    } catch {
      setShareState('idle');
    }
  };

  const shareLabel =
    shareState === 'copied' ? 'Copied' : shareState === 'sharing' ? 'Sharing…' : 'Share';

  const stateLabel = isActive
    ? 'Now playing'
    : edition.status === 'ready'
      ? 'Ready'
      : edition.status === 'expired'
        ? 'Expired'
        : edition.status === 'failed'
          ? 'Failed'
          : edition.status === 'processing'
            ? 'Converting'
            : 'Queued';

  const rightFooter = isActive && edition.remainingSeconds != null
    ? formatRemaining(edition.remainingSeconds)
    : formatDurationMinutes(edition.durationSeconds);

  return (
    <article className={cardClass}>
      <div className={styles.body}>
        <div className={styles.topRow}>
          <span className={styles.editionNum}>{editionNumberLabel(edition.editionNumber)}</span>
          <span className={stateClass}>
            <span className={styles.stateDot} aria-hidden="true" />
            {stateLabel}
          </span>
        </div>

        <h3 className={styles.title}>{edition.title}</h3>

        <div className={styles.rule} />

        <div className={styles.fields}>
          {edition.author && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Author</span>
              <span className={styles.fieldValue}>{edition.author}</span>
            </div>
          )}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Source</span>
            {isLinkableUrl(edition.sourceUrl) ? (
              <a
                href={edition.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.fieldValue} ${styles.fieldLink}`}
              >
                {edition.source}
              </a>
            ) : (
              <span className={styles.fieldValue}>{edition.source}</span>
            )}
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Duration</span>
            <span className={styles.fieldValue}>{formatDurationMinutes(edition.durationSeconds)}</span>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Prepared</span>
            <span className={styles.fieldValue}>{formatShortDate(edition.publishedAt)}</span>
          </div>
          {edition.expiresAt && (edition.status === 'ready' || edition.status === 'expired') && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{edition.status === 'expired' ? 'Expired' : 'Expires'}</span>
              <span className={styles.fieldValue}>{formatShortDate(edition.expiresAt)}</span>
            </div>
          )}
        </div>

        <p className={styles.serial}>
          {edition.serialNumber} · {editionType}
        </p>
      </div>

      <footer className={styles.footer}>
        <div className={styles.actions}>
          {edition.status === 'expired' ? (
            <button
              type="button"
              className={styles.footerAction}
              onClick={handleRenewClick}
              disabled={!onRenew || renewState === 'renewing'}
              aria-label={`Renew ${edition.title}`}
            >
              {renewState === 'renewing' ? 'Renewing…' : 'Renew'}
            </button>
          ) : (
            <button
              type="button"
              className={styles.footerAction}
              onClick={() => onPlay?.(edition.id)}
              disabled={edition.status !== 'ready' || !onPlay}
              aria-label={`Play ${edition.title}`}
            >
              {isActive ? 'Playing' : 'Play'}
            </button>
          )}
          <span className={styles.actionDivider} aria-hidden="true" />
          <button
            type="button"
            className={`${styles.footerAction} ${styles.footerActionDanger}`}
            onClick={() => onDelete?.(edition.id)}
            disabled={!onDelete}
            aria-label={`Delete ${edition.title}`}
          >
            Delete
          </button>
          <span className={styles.actionDivider} aria-hidden="true" />
          <button
            type="button"
            className={styles.footerAction}
            onClick={() => onViewTranscript?.(edition.id)}
            disabled={edition.status !== 'ready' || !onViewTranscript}
          >
            Transcript
          </button>
          <span className={styles.actionDivider} aria-hidden="true" />
          <button
            type="button"
            className={styles.footerAction}
            onClick={handleDownloadClick}
            disabled={edition.status !== 'ready' || !onDownload || downloadState === 'downloading'}
            aria-label={`Download ${edition.title}`}
          >
            {downloadLabel}
          </button>
          <span className={styles.actionDivider} aria-hidden="true" />
          <button
            type="button"
            className={styles.footerAction}
            onClick={handleShareClick}
            disabled={edition.status !== 'ready' || !onShare || shareState === 'sharing'}
            aria-label={`Share ${edition.title}`}
          >
            {shareLabel}
          </button>
          <span className={styles.actionDivider} aria-hidden="true" />
          <button
            type="button"
            className={`${styles.footerAction} ${styles.footerActionDanger}`}
            onClick={() => onReport?.(edition.id)}
            disabled={
              (edition.status !== 'ready' && edition.status !== 'failed') ||
              !onReport ||
              edition.reported
            }
            aria-label={
              edition.reported
                ? `${edition.title} has been reported`
                : `Report a problem with ${edition.title}`
            }
          >
            {edition.reported ? 'Reported' : 'Report'}
          </button>
        </div>
        <span className={styles.duration}>{rightFooter}</span>
      </footer>
    </article>
  );
}
